import { billingToday, parseBillingRange } from '@smart-link/shared';
import { addDays, endOfMonth, format, startOfMonth, startOfWeek, subMonths } from 'date-fns';

export type BillingPreset =
  | 'week'
  | 'fortnight'
  | 'month'
  | 'thisWeek'
  | 'lastWeek'
  | 'thisMonth'
  | 'lastMonth'
  | 'custom';

export const BILLING_PRESETS: { id: Exclude<BillingPreset, 'custom'>; label: string }[] = [
  { id: 'week', label: '近 7 天' },
  { id: 'fortnight', label: '近 14 天' },
  { id: 'month', label: '近 30 天' },
  { id: 'thisWeek', label: '本周' },
  { id: 'lastWeek', label: '上周' },
  { id: 'thisMonth', label: '本月' },
  { id: 'lastMonth', label: '上月' },
];

// Calendar dates use local components; the API receives date keys in UTC+8,
// never browser-local epoch boundaries (which differ for overseas operators).
export function calendarDate(key: string): Date {
  const [year, month, day] = key.split('-').map(Number);
  return new Date(year!, month! - 1, day!);
}

export function presetRange(preset: Exclude<BillingPreset, 'custom'>, todayKey = billingToday()) {
  const today = calendarDate(todayKey);
  const weekStart = startOfWeek(today, { weekStartsOn: 1 });
  let from = today;
  let to = today;
  switch (preset) {
    case 'week':
      from = addDays(today, -6);
      break;
    case 'fortnight':
      from = addDays(today, -13);
      break;
    case 'month':
      from = addDays(today, -29);
      break;
    case 'thisWeek':
      from = weekStart;
      break;
    case 'lastWeek':
      from = addDays(weekStart, -7);
      to = addDays(weekStart, -1);
      break;
    case 'thisMonth':
      from = startOfMonth(today);
      break;
    case 'lastMonth':
      from = startOfMonth(subMonths(today, 1));
      to = endOfMonth(from);
      break;
  }
  return parseBillingRange(format(from, 'yyyy-MM-dd'), format(to, 'yyyy-MM-dd'))!;
}

export function rangeLabel(from: string, to: string): string {
  return `${from.replaceAll('-', '/')} — ${to.replaceAll('-', '/')}`;
}

export const formatVisits = (value: number) => new Intl.NumberFormat('zh-CN').format(value);
export const billingQuery = (from: string, to: string) => new URLSearchParams({ from, to });
